// Doc-Ref: docs/features/collaborations-current.md
import { useState, useCallback } from "react";

const STORAGE_KEY = "collab_recent_entities";
const MAX_RECENT = 5;

/**
 * Reads recent entities from localStorage.
 * @returns {Array} List of recent entity objects.
 */
const readFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/**
 * Persists recent entities to localStorage.
 * @param {Array} list
 */
const writeToStorage = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Silently ignore storage errors (e.g. private browsing quota)
  }
};

/**
 * Custom hook for managing recent collaboration entity searches.
 *
 * Stores up to MAX_RECENT entities (FIFO) in localStorage,
 * deduplicating by entity id.
 *
 * @returns {{ recentEntities: Array, addRecentEntity: Function, clearRecentEntities: Function }}
 */
const useRecentEntities = () => {
  const [recentEntities, setRecentEntities] = useState(() => readFromStorage());

  /**
   * Add an entity to the recent list.
   * Deduplicates by id — the new entry moves to the top.
   * @param {{ id: string, name: string, type: string, [key: string]: any }} entity
   */
  const addRecentEntity = useCallback((entity) => {
    if (!entity?.id) return;

    setRecentEntities((prev) => {
      // Remove existing entry with the same id (dedup)
      const filtered = prev.filter((e) => e.id !== entity.id);
      // Prepend new entry and cap at MAX_RECENT
      const updated = [
        { id: entity.id, name: entity.name, type: entity.type },
        ...filtered,
      ].slice(0, MAX_RECENT);

      writeToStorage(updated);
      return updated;
    });
  }, []);

  /**
   * Clear all recent entities.
   */
  const clearRecentEntities = useCallback(() => {
    writeToStorage([]);
    setRecentEntities([]);
  }, []);

  return { recentEntities, addRecentEntity, clearRecentEntities };
};

export default useRecentEntities;
